import { createInstance, type ModuleFederation } from "@module-federation/runtime";
import React from "react";
import ReactDOM from "react-dom";
import * as jsxRuntime from "react/jsx-runtime";
import * as jsxDevRuntime from "react/jsx-dev-runtime";
import { ErrorBoundary } from "@glide/common";
import { getLocationSettings } from "@glide/common-core/dist/js/location";

interface RemoteLoaderWithBoundary {
    readonly loading: React.ReactNode;
    readonly error: React.ReactNode;
    readonly children: React.ReactNode;
}

/**
 * A convenience wrapper for remote loading
 * Note that each component should have its own ErrorBoundary
 * Otherwise a single loading failure can result in all remote modules
 * in failure states.
 */
export const SuspenseAndErrorFallback = (p: RemoteLoaderWithBoundary) => {
    const { loading, error, children } = p;

    return (
        <ErrorBoundary fallback={error}>
            <React.Suspense fallback={loading}>{children}</React.Suspense>
        </ErrorBoundary>
    );
};

// "fun" wrapper (S)uspense (a)nd (f)allback for (e)rror
export const Safe = SuspenseAndErrorFallback;

export const ModuleLoader = () => {
    return <div tw="text-text-pale p-4">Loading module...</div>;
};

export const ModuleError = ({ module }: { module: string }) => {
    return <div tw="text-text-pale p-4">Error loading module: {module}</div>;
};

/**
 * Singleton class for loading remote modules using Module Federation.
 * Handles dynamic registration of remote modules and provides lazy loading capabilities.
 */
export class RemoteModuleLoader {
    private static instance: RemoteModuleLoader | undefined;
    private registeredRemotes: Map<string, string> = new Map();
    private moduleFederation: ModuleFederation | undefined;
    /**
     * Initializes Module Federation runtime on first access.
     */
    static get(): RemoteModuleLoader {
        if (this.instance === undefined) {
            this.instance = new RemoteModuleLoader();
            this.instance.initializeModuleFederation();
        }

        return this.instance;
    }

    private initializeModuleFederation = () => {
        this.moduleFederation = createInstance({
            name: "glide-builder-runtime-federation-instance",
            remotes: [],
            shared: {
                react: {
                    version: React.version,
                    lib: () => React,
                    shareConfig: { singleton: true, requiredVersion: React.version },
                },
                "react-dom": {
                    version: ReactDOM.version,
                    lib: () => ReactDOM,
                    shareConfig: { singleton: true, requiredVersion: ReactDOM.version },
                },
                "react/jsx-runtime": {
                    version: React.version,
                    lib: () => jsxRuntime,
                    shareConfig: { singleton: true, requiredVersion: React.version },
                },
                "react/jsx-dev-runtime": {
                    version: React.version,
                    lib: () => jsxDevRuntime,
                    shareConfig: { singleton: true, requiredVersion: React.version },
                },
            },
        });
    };

    /**
     * Loads a remote component with a specific entry point URL.
     * Registers the remote dynamically if not already registered.
     *
     * @param remoteName - The name of the remote module (can be kebab-case or snake_case)
     * @param componentPath - The path to the component within the remote module
     * @param entryPoint - The URL to the remote entry point (remoteEntry.js)
     * @returns A React lazy component that loads the remote module
     * @template T - The props type for the remote component, usually supplied by the ui package
     */
    public loadRemoteComponentWithEntryPoint<T = any>(remoteName: string, componentPath: string, entryPoint: string) {
        // remote module federation runtime will NOT override an existing key
        // so it's nice to warn folks in case they are confused.
        const remoteNameKey = this.remoteName(remoteName);
        if (this.registeredRemotes.has(remoteNameKey)) {
            // eslint-disable-next-line no-console
            console.warn(`Remote ${remoteName} was already registered with entry point ${entryPoint}`);
        }

        this.moduleFederation?.registerRemotes([
            {
                name: remoteNameKey,
                entry: entryPoint,
            },
        ]);

        this.registeredRemotes.set(remoteNameKey, entryPoint);
        return this.lazilyLoadComponent<T>(remoteName, componentPath);
    }

    /**
     * If the remote is already registered, returns the registered entry point.
     * Otherwise, constructs the default GCS bucket URL.
     */
    public getDefaultEntryPoint = (name: string): string => {
        const remoteNameKey = this.remoteName(name);

        // You can't wrap .has and return .get in typescript.
        const registeredKey = this.registeredRemotes.get(remoteNameKey);
        if (registeredKey !== undefined) {
            return registeredKey;
        }

        const gcsDirectory = this.moduleName(name);
        const gcsBucket = getLocationSettings().remoteModulesGCSContainer;
        const entryPoint = `${gcsBucket}${gcsDirectory}/latest/remoteEntry.js`;
        return entryPoint;
    };

    /**
     * Loads a remote component using the default entry point.
     * If the remote hasn't been registered, uses the environment-configured GCS bucket URL.
     *
     * @param remoteName - The name of the remote module (can be kebab-case or snake_case)
     * @param componentPath - The path to the component within the remote module
     * @returns A React lazy component that loads the remote module
     * @template T - The props type for the remote component
     */
    public loadRemoteComponent = <T = any,>(remoteName: string, componentPath: string) => {
        if (!this.registeredRemotes.has(this.remoteName(remoteName))) {
            return this.loadRemoteComponentWithEntryPoint(
                remoteName,
                componentPath,
                this.getDefaultEntryPoint(remoteName)
            );
        }

        return this.lazilyLoadComponent<T>(remoteName, componentPath);
    };

    /**
     * Converts a module name to the webpack remote name standard (snake_case).
     */
    private remoteName = (name: string) => {
        return name.replace(/-/g, "_");
    };

    /**
     * Converts a remote name to the module name standard (kebab-case).
     */
    private moduleName = (name: string) => {
        return name.replace(/_/g, "-");
    };

    private lazilyLoadComponent = <T = any,>(remoteName: string, componentPath: string) => {
        return React.lazy(() =>
            Promise.all([
                this.moduleFederation?.loadRemote(`${remoteName}/styles`).catch(e => {
                    // eslint-disable-next-line no-console
                    console.warn("Failed to load styles for", remoteName, e);
                }),
                this.moduleFederation?.loadRemote(`${remoteName}/${componentPath}`),
            ])
                .then(([_, module]) => {
                    if (module && typeof module === "object" && "default" in module && module.default) {
                        return { default: module.default as React.ComponentType<T> };
                    }
                    throw new Error(`Invalid module format for ${remoteName}/${componentPath}`);
                })
                .catch(err => {
                    // eslint-disable-next-line no-console
                    console.error(`Error loading ${remoteName}/${componentPath}:`, err);

                    // just return () => null here, we already logged the error.
                    return {
                        default: (() => null) as React.ComponentType<T>,
                    };
                })
        );
    };
}
